import type { Point, Shape, ImageScale, PixelSpacing } from '../types';

// Generate unique ID for shapes
export function generateShapeId(): string {
  return `shape_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

// Convert canvas coordinates to image coordinates accounting for scaling, zoom, and panning
export function canvasToImageCoordinates(
  canvasPoint: Point,
  imageScale: ImageScale
): Point {
  const effectiveScaleX = imageScale.scaleX * imageScale.zoomLevel;
  const effectiveScaleY = imageScale.scaleY * imageScale.zoomLevel;

  return {
    x: (canvasPoint.x - imageScale.offsetX - imageScale.panX) / effectiveScaleX,
    y: (canvasPoint.y - imageScale.offsetY - imageScale.panY) / effectiveScaleY,
  };
}

// Convert image coordinates to canvas coordinates accounting for zoom and panning
export function imageToCanvasCoordinates(
  imagePoint: Point,
  imageScale: ImageScale
): Point {
  const effectiveScaleX = imageScale.scaleX * imageScale.zoomLevel;
  const effectiveScaleY = imageScale.scaleY * imageScale.zoomLevel;

  return {
    x: imagePoint.x * effectiveScaleX + imageScale.offsetX + imageScale.panX,
    y: imagePoint.y * effectiveScaleY + imageScale.offsetY + imageScale.panY,
  };
}

// Calculate distance between two points in pixels
export function calculatePixelDistance(point1: Point, point2: Point): number {
  const dx = point2.x - point1.x;
  const dy = point2.y - point1.y;
  return Math.sqrt(dx * dx + dy * dy);
}

// Convert pixel distance to real-world distance (cm)
// Note: pixelSpacing is in mm, so we divide by 10 to convert to cm
export function pixelsToRealWorld(
  pixelDistance: number,
  pixelSpacing: PixelSpacing,
  direction: 'x' | 'y' | 'diagonal' = 'diagonal'
): number {
  let mmValue: number;
  switch (direction) {
    case 'x':
      mmValue = pixelDistance * pixelSpacing.x;
      break;
    case 'y':
      mmValue = pixelDistance * pixelSpacing.y;
      break;
    case 'diagonal':
      // For diagonal measurements, use average pixel spacing
      const avgSpacing = (pixelSpacing.x + pixelSpacing.y) / 2;
      mmValue = pixelDistance * avgSpacing;
      break;
    default:
      mmValue = pixelDistance * pixelSpacing.x;
  }
  // Convert mm to cm
  return mmValue / 10;
}

// Calculate real-world distance between two points
export function calculateRealWorldDistance(
  point1: Point,
  point2: Point,
  pixelSpacing: PixelSpacing
): number {
  const pixelDist = calculatePixelDistance(point1, point2);
  return pixelsToRealWorld(pixelDist, pixelSpacing);
}

// Check if a point is near a line (for selection/editing)
export function isPointNearLine(
  point: Point,
  lineStart: Point,
  lineEnd: Point,
  tolerance: number = 5
): boolean {
  const dist = distancePointToLine(point, lineStart, lineEnd);
  return dist <= tolerance;
}

// Check if point hits a shape (for selection)
export function isPointInShape(
  point: Point,
  shape: Shape,
  tolerance: number = 5
): boolean {
  switch (shape.type) {
    case 'line':
      const lineShape = shape as Extract<Shape, { type: 'line' }>;
      return isPointNearLine(
        point,
        lineShape.startPoint,
        lineShape.endPoint,
        tolerance
      );

    case 'square':
      const rectShape = shape as Extract<Shape, { type: 'square' }>;
      return isPointInRectangle(
        point,
        rectShape.startPoint,
        rectShape.endPoint
      );

    case 'circle':
      const circleShape = shape as Extract<Shape, { type: 'circle' }>;
      return isPointInEllipse(
        point,
        circleShape.center,
        circleShape.radiusX,
        circleShape.radiusY,
        circleShape.rotation ?? 0
      );

    case 'freehand':
      const freehandShape = shape as Extract<Shape, { type: 'freehand' }>;
      // Check if point is near any segment of the freehand path
      return isPointNearPolygonEdge(point, freehandShape.points, tolerance);

    default:
      return false;
  }
}

// Move a shape by a delta offset
export function moveShape(shape: Shape, deltaX: number, deltaY: number): Shape {
  switch (shape.type) {
    case 'line':
      const lineShape = shape as Extract<Shape, { type: 'line' }>;
      return {
        ...lineShape,
        startPoint: {
          x: lineShape.startPoint.x + deltaX,
          y: lineShape.startPoint.y + deltaY,
        },
        endPoint: {
          x: lineShape.endPoint.x + deltaX,
          y: lineShape.endPoint.y + deltaY,
        },
      };

    case 'square':
      const rectShape = shape as Extract<Shape, { type: 'square' }>;
      return {
        ...rectShape,
        startPoint: {
          x: rectShape.startPoint.x + deltaX,
          y: rectShape.startPoint.y + deltaY,
        },
        endPoint: {
          x: rectShape.endPoint.x + deltaX,
          y: rectShape.endPoint.y + deltaY,
        },
      };

    case 'circle':
      const circleShape = shape as Extract<Shape, { type: 'circle' }>;
      return {
        ...circleShape,
        center: {
          x: circleShape.center.x + deltaX,
          y: circleShape.center.y + deltaY,
        },
      };

    case 'freehand':
      const freehandShape = shape as Extract<Shape, { type: 'freehand' }>;
      return {
        ...freehandShape,
        points: freehandShape.points.map((point) => ({
          x: point.x + deltaX,
          y: point.y + deltaY,
        })),
      };

    default:
      return shape;
  }
}

// Calculate distance from point to line
function distancePointToLine(
  point: Point,
  lineStart: Point,
  lineEnd: Point
): number {
  const A = point.x - lineStart.x;
  const B = point.y - lineStart.y;
  const C = lineEnd.x - lineStart.x;
  const D = lineEnd.y - lineStart.y;

  const dot = A * C + B * D;
  const lenSq = C * C + D * D;

  if (lenSq === 0) {
    return Math.sqrt(A * A + B * B);
  }

  const param = dot / lenSq;

  let xx: number, yy: number;

  if (param < 0) {
    xx = lineStart.x;
    yy = lineStart.y;
  } else if (param > 1) {
    xx = lineEnd.x;
    yy = lineEnd.y;
  } else {
    xx = lineStart.x + param * C;
    yy = lineStart.y + param * D;
  }

  const dx = point.x - xx;
  const dy = point.y - yy;
  return Math.sqrt(dx * dx + dy * dy);
}

// Check if point is inside rectangle
export function isPointInRectangle(
  point: Point,
  rectStart: Point,
  rectEnd: Point
): boolean {
  const minX = Math.min(rectStart.x, rectEnd.x);
  const maxX = Math.max(rectStart.x, rectEnd.x);
  const minY = Math.min(rectStart.y, rectEnd.y);
  const maxY = Math.max(rectStart.y, rectEnd.y);

  return (
    point.x >= minX && point.x <= maxX && point.y >= minY && point.y <= maxY
  );
}

// Check if point is inside ellipse (optionally rotated; rotation in radians)
export function isPointInEllipse(
  point: Point,
  center: Point,
  radiusX: number,
  radiusY: number,
  rotation: number = 0
): boolean {
  let dx = point.x - center.x;
  let dy = point.y - center.y;
  if (rotation !== 0) {
    const cos = Math.cos(-rotation);
    const sin = Math.sin(-rotation);
    const tx = dx * cos - dy * sin;
    const ty = dx * sin + dy * cos;
    dx = tx;
    dy = ty;
  }
  return (dx * dx) / (radiusX * radiusX) + (dy * dy) / (radiusY * radiusY) <= 1;
}

// Check if point is inside polygon using ray casting algorithm
export function isPointInPolygon(
  point: Point,
  polygonPoints: Point[]
): boolean {
  if (polygonPoints.length < 3) return false;

  let inside = false;
  const x = point.x;
  const y = point.y;

  for (
    let i = 0, j = polygonPoints.length - 1;
    i < polygonPoints.length;
    j = i++
  ) {
    const xi = polygonPoints[i].x;
    const yi = polygonPoints[i].y;
    const xj = polygonPoints[j].x;
    const yj = polygonPoints[j].y;

    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }

  return inside;
}

// Check if point is near polygon edge (for selection)
export function isPointNearPolygonEdge(
  point: Point,
  polygonPoints: Point[],
  tolerance: number = 5
): boolean {
  if (polygonPoints.length < 2) return false;

  for (let i = 0; i < polygonPoints.length; i++) {
    const startPoint = polygonPoints[i];
    const endPoint = polygonPoints[(i + 1) % polygonPoints.length];

    if (isPointNearLine(point, startPoint, endPoint, tolerance)) {
      return true;
    }
  }

  return false;
}

// Calculate line length in real-world units (mm)
export function calculateLineLength(
  startPoint: Point,
  endPoint: Point,
  pixelSpacing?: PixelSpacing
): number {
  if (!pixelSpacing) {
    return calculatePixelDistance(startPoint, endPoint);
  }
  return calculateRealWorldDistance(startPoint, endPoint, pixelSpacing);
}

// Calculate rectangle area in real-world units (cm²) or pixels²
// Note: pixelSpacing is in mm, so we convert to cm² (divide by 100)
export function calculateRectangleArea(
  startPoint: Point,
  endPoint: Point,
  pixelSpacing?: PixelSpacing
): number {
  const widthPixels = Math.abs(endPoint.x - startPoint.x);
  const heightPixels = Math.abs(endPoint.y - startPoint.y);

  if (!pixelSpacing) {
    return widthPixels * heightPixels;
  }

  const widthMm = widthPixels * pixelSpacing.x;
  const heightMm = heightPixels * pixelSpacing.y;

  // Convert mm² to cm² (divide by 100)
  return (widthMm * heightMm) / 100;
}

// Rectangle perimeter: 2*(width+height). Returns cm if pixelSpacing, else pixels.
export function calculateRectanglePerimeter(
  startPoint: Point,
  endPoint: Point,
  pixelSpacing?: PixelSpacing
): number {
  const widthPixels = Math.abs(endPoint.x - startPoint.x);
  const heightPixels = Math.abs(endPoint.y - startPoint.y);
  const perimeterPixels = 2 * (widthPixels + heightPixels);

  if (!pixelSpacing) {
    return perimeterPixels;
  }
  const widthMm = widthPixels * pixelSpacing.x;
  const heightMm = heightPixels * pixelSpacing.y;
  const perimeterMm = 2 * (widthMm + heightMm);
  return perimeterMm / 10; // mm -> cm
}

/** Pixel spacing from DICOM is mm unless explicitly marked cm. */
export function effectivePixelSpacingUnit(
  unit?: string | null
): 'mm' | 'cm' {
  return unit === 'cm' ? 'cm' : 'mm';
}

// Calculate ellipse perimeter in real-world units (mm) or pixels using Ramanujan's approximation
/** Ramanujan approximation for ellipse perimeter. With pixelSpacing (mm) returns cm when pixel_spacing_unit is 'mm', else same unit as radii. Without pixelSpacing returns pixels. Matches backend _ellipse_perimeter_cm when converted. */
export function calculateEllipsePerimeter(
  radiusX: number,
  radiusY: number,
  pixelSpacing?: PixelSpacing,
  pixel_spacing_unit?: string
): number {
  const spacingUnit = pixelSpacing
    ? effectivePixelSpacingUnit(pixel_spacing_unit)
    : undefined;
  const pixelSpacingRatio = spacingUnit === 'mm' ? 0.1 : 1;
  const aReal = pixelSpacing ? radiusX * pixelSpacing.x : radiusX;
  const bReal = pixelSpacing ? radiusY * pixelSpacing.y : radiusY;
  const h = Math.pow((aReal - bReal) / (aReal + bReal), 2);
  return (Math.PI * (aReal + bReal) * (1 + (3 * h) / (10 + Math.sqrt(4 - 3 * h)))) * pixelSpacingRatio;
}

// Calculate ellipse area in real-world units (cm²) or pixels²
// Note: pixelSpacing is in mm, so we convert to cm² (divide by 100)
export function calculateEllipseArea(
  radiusX: number,
  radiusY: number,
  pixelSpacing?: PixelSpacing
): number {
  // Convert radii to real-world units (mm) or use pixels
  const aReal = pixelSpacing ? radiusX * pixelSpacing.x : radiusX;
  const bReal = pixelSpacing ? radiusY * pixelSpacing.y : radiusY;

  const areaMmSq = Math.PI * aReal * bReal;

  // Convert mm² to cm² (divide by 100) if using pixel spacing
  return pixelSpacing ? areaMmSq / 100 : areaMmSq;
}

// Calculate polygon perimeter in real-world units (mm) or pixels
// This is for CLOSED polygons - it wraps around from last point to first
export function calculatePolygonPerimeter(
  points: Point[],
  pixelSpacing?: PixelSpacing
): number {
  if (points.length < 2) return 0;

  let perimeter = 0;
  for (let i = 0; i < points.length; i++) {
    const startPoint = points[i];
    const endPoint = points[(i + 1) % points.length];
    if (pixelSpacing) {
      perimeter += calculateRealWorldDistance(
        startPoint,
        endPoint,
        pixelSpacing
      );
    } else {
      perimeter += calculatePixelDistance(startPoint, endPoint);
    }
  }

  return perimeter;
}

// Calculate open polyline length in real-world units (mm) or pixels
// This is for OPEN polylines - it does NOT wrap around from last point to first
export function calculatePolylineLength(
  points: Point[],
  pixelSpacing?: PixelSpacing
): number {
  if (points.length < 2) return 0;

  let length = 0;
  for (let i = 0; i < points.length - 1; i++) {
    const startPoint = points[i];
    const endPoint = points[i + 1];
    if (pixelSpacing) {
      length += calculateRealWorldDistance(startPoint, endPoint, pixelSpacing);
    } else {
      length += calculatePixelDistance(startPoint, endPoint);
    }
  }

  return length;
}

// Calculate polygon area in real-world units (cm²) or pixels² using shoelace formula
// Note: pixelSpacing is in mm, so we convert to cm² (divide by 100)
export function calculatePolygonArea(
  points: Point[],
  pixelSpacing?: PixelSpacing
): number {
  if (points.length < 3) return 0;

  let area = 0;
  for (let i = 0; i < points.length; i++) {
    const j = (i + 1) % points.length;
    // Convert to real-world coordinates (mm) or use pixels
    const xi = pixelSpacing ? points[i].x * pixelSpacing.x : points[i].x;
    const yi = pixelSpacing ? points[i].y * pixelSpacing.y : points[i].y;
    const xj = pixelSpacing ? points[j].x * pixelSpacing.x : points[j].x;
    const yj = pixelSpacing ? points[j].y * pixelSpacing.y : points[j].y;

    area += xi * yj - xj * yi;
  }

  const areaMmSq = Math.abs(area) / 2;

  // Convert mm² to cm² (divide by 100) if using pixel spacing
  return pixelSpacing ? areaMmSq / 100 : areaMmSq;
}

/** Endpoints "touch" (hand-drawn / AI) — Euclidean distance in image px. */
export const FREEHAND_CLOSURE_ENDPOINT_EPS_PX = 4;

/** True duplicate closing vertex (we appended first again) — strip before simplify/render ring. */
const FREEHAND_DUPLICATE_VERTEX_EPS_PX = 0.85;

/** True when first and last are the same vertex (duplicate closing row), including A–B–A. */
export function freehandClosingDuplicatePresent(points: Point[]): boolean {
  if (points.length < 3) return false;
  return (
    calculatePixelDistance(points[0], points[points.length - 1]) <
    FREEHAND_DUPLICATE_VERTEX_EPS_PX
  );
}

/** Shape is treated as a closed region (measurement / hit-test / simplify closure). */
export function isFreehandShapeClosed(points: Point[]): boolean {
  if (points.length < 3) return false;
  return (
    calculatePixelDistance(points[0], points[points.length - 1]) <
    FREEHAND_CLOSURE_ENDPOINT_EPS_PX
  );
}

/**
 * Vertices for stroking a smooth freehand path (image space). Drops a redundant
 * duplicate closing point when present. With ≥3 vertices, the stroke is always
 * treated as a closed loop so contours stay visually closed after simplify / API round-trips.
 */
export function getFreehandStrokeClosure(points: Point[]): {
  ring: Point[];
  strokeClosed: boolean;
} {
  if (points.length < 2) {
    return { ring: points.slice(), strokeClosed: false };
  }
  const treatAsClosed = points.length >= 3;
  const ring = freehandClosingDuplicatePresent(points)
    ? points.slice(0, -1)
    : points.slice();

  return {
    ring,
    strokeClosed: treatAsClosed && ring.length >= 2,
  };
}

/** Shortest distance from `p` to the segment `a`–`b` (image pixels). */
function perpendicularDistanceToSegment(p: Point, a: Point, b: Point): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq < 1e-14) return calculatePixelDistance(p, a);
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  const px = a.x + t * dx;
  const py = a.y + t * dy;
  return Math.hypot(p.x - px, p.y - py);
}

/**
 * Douglas–Peucker polyline simplification (open chain: keeps first and last).
 */
export function douglasPeucker(points: Point[], epsilon: number): Point[] {
  if (points.length <= 2) {
    return points.map((q) => ({ x: q.x, y: q.y }));
  }

  const first = points[0];
  const last = points[points.length - 1];
  let maxDist = 0;
  let maxIdx = 0;

  for (let i = 1; i < points.length - 1; i++) {
    const d = perpendicularDistanceToSegment(points[i], first, last);
    if (d > maxDist) {
      maxDist = d;
      maxIdx = i;
    }
  }

  if (maxDist > epsilon) {
    const left = douglasPeucker(points.slice(0, maxIdx + 1), epsilon);
    const right = douglasPeucker(points.slice(maxIdx), epsilon);
    return [...left.slice(0, -1), ...right];
  }

  return [
    { x: first.x, y: first.y },
    { x: last.x, y: last.y },
  ];
}

/** Default tolerance in image pixels (slightly tighter than draw sampling so curves stay faithful after smoothing). */
export const FREEHAND_DOUGLAS_PEUCKER_EPSILON = 1.5;

/**
 * Simplifies a freehand stroke for storage/rendering. Strokes with ≥3 vertices are
 * treated as closed regions: after Douglas–Peucker, the first point is repeated at
 * the end (unless it already matches the last). Degenerate simplification falls back
 * to the original points.
 */
export function simplifyFreehandPoints(
  points: Point[],
  epsilon: number = FREEHAND_DOUGLAS_PEUCKER_EPSILON
): Point[] {
  if (points.length <= 2) {
    return points.map((p) => ({ x: p.x, y: p.y }));
  }

  const regionClosed = points.length >= 3;
  const core = freehandClosingDuplicatePresent(points)
    ? points.slice(0, -1)
    : points.slice();

  if (core.length <= 2) {
    return points.map((p) => ({ x: p.x, y: p.y }));
  }

  let simplified = douglasPeucker(core, epsilon);

  if (simplified.length < 2) {
    simplified = [
      { x: core[0].x, y: core[0].y },
      { x: core[core.length - 1].x, y: core[core.length - 1].y },
    ];
  }

  if (regionClosed) {
    if (simplified.length < 3) {
      return points.map((p) => ({ x: p.x, y: p.y }));
    }
    const out = simplified.map((p) => ({ x: p.x, y: p.y }));
    const f = out[0];
    const last = out[out.length - 1];
    if (calculatePixelDistance(f, last) >= FREEHAND_DUPLICATE_VERTEX_EPS_PX) {
      out.push({ x: f.x, y: f.y });
    }
    return out;
  }

  return simplified.map((p) => ({ x: p.x, y: p.y }));
}

/**
 * Builds a smoothed freehand stroke using quadratic curves (canvas pixel space).
 * `ring` is vertex list only — no duplicate closing point.
 * When `closed`, draws a smooth loop (use with stroke(); do not fill unless intended).
 */
export function buildSmoothFreehandCanvasPolyline(
  ctx: CanvasRenderingContext2D,
  ring: Point[],
  closed: boolean
): void {
  if (ring.length < 2) return;

  if (closed && ring.length === 2) {
    const a = ring[0];
    const b = ring[1];
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
    ctx.lineTo(a.x, a.y);
    ctx.closePath();
    return;
  }

  if (!closed || ring.length < 3) {
    if (ring.length === 2) {
      ctx.moveTo(ring[0].x, ring[0].y);
      ctx.lineTo(ring[1].x, ring[1].y);
      return;
    }
    ctx.moveTo(ring[0].x, ring[0].y);
    for (let i = 1; i < ring.length - 2; i++) {
      const xc = (ring[i].x + ring[i + 1].x) / 2;
      const yc = (ring[i].y + ring[i + 1].y) / 2;
      ctx.quadraticCurveTo(ring[i].x, ring[i].y, xc, yc);
    }
    ctx.quadraticCurveTo(
      ring[ring.length - 2].x,
      ring[ring.length - 2].y,
      ring[ring.length - 1].x,
      ring[ring.length - 1].y
    );
    return;
  }

  const n = ring.length;
  ctx.moveTo((ring[n - 1].x + ring[0].x) / 2, (ring[n - 1].y + ring[0].y) / 2);
  for (let i = 0; i < n; i++) {
    const p = ring[i];
    const np = ring[(i + 1) % n];
    ctx.quadraticCurveTo(p.x, p.y, (p.x + np.x) / 2, (p.y + np.y) / 2);
  }
  ctx.closePath();
}

/**
 * Same as {@link buildSmoothFreehandCanvasPolyline} but converts image-space points first.
 * `imagePoints` may include a duplicate closing vertex when the shape is closed.
 */
export function buildSmoothFreehandPath(
  ctx: CanvasRenderingContext2D,
  imagePoints: Point[],
  imageScale: ImageScale
): void {
  const { ring: ringIm, strokeClosed } = getFreehandStrokeClosure(imagePoints);
  if (ringIm.length < 2) return;
  const ring = ringIm.map((p) => imageToCanvasCoordinates(p, imageScale));
  buildSmoothFreehandCanvasPolyline(ctx, ring, strokeClosed);
}

// Get shape color based on type - vibrant colors for better visibility
export function getShapeColor(
  shapeType: string,
  isSelected: boolean = false
): string {
  if (isSelected) {
    return '#3b82f6'; // Bright blue for selected shapes
  }

  switch (shapeType) {
    case 'line':
      return '#10b981'; // Emerald green for lines
    case 'square':
      return '#8b5cf6'; // Violet for rectangles
    case 'circle':
      return '#ec4899'; // Pink for circles
    case 'freehand':
      return '#f59e0b'; // Amber for freehand
    default:
      return '#6b7280'; // Gray for unknown types
  }
}

// Format measurement value for display
export function formatMeasurement(value: number, unit: string): string {
  if (value < 10) {
    return `${value.toFixed(2)} ${unit}`;
  } else if (value < 100) {
    return `${value.toFixed(1)} ${unit}`;
  } else {
    return `${Math.round(value)} ${unit}`;
  }
}

// Check if point is near a control handle
export function isPointNearHandle(
  point: Point,
  handlePoint: Point,
  tolerance: number = 8
): boolean {
  const distance = calculatePixelDistance(point, handlePoint);
  return distance <= tolerance;
}

// Get control points for a shape (in image coordinates)
export function getShapeControlPoints(shape: Shape): { [key: string]: Point } {
  switch (shape.type) {
    case 'line':
      const lineShape = shape as Extract<Shape, { type: 'line' }>;
      return {
        start: lineShape.startPoint,
        end: lineShape.endPoint,
      };

    case 'square':
      const rectShape = shape as Extract<Shape, { type: 'square' }>;
      const minX = Math.min(rectShape.startPoint.x, rectShape.endPoint.x);
      const maxX = Math.max(rectShape.startPoint.x, rectShape.endPoint.x);
      const minY = Math.min(rectShape.startPoint.y, rectShape.endPoint.y);
      const maxY = Math.max(rectShape.startPoint.y, rectShape.endPoint.y);

      return {
        topLeft: { x: minX, y: minY },
        topRight: { x: maxX, y: minY },
        bottomLeft: { x: minX, y: maxY },
        bottomRight: { x: maxX, y: maxY },
      };

    case 'circle': {
      const circleShape = shape as Extract<Shape, { type: 'circle' }>;
      const r = circleShape.rotation ?? 0;
      const c = circleShape.center;
      const rx = circleShape.radiusX;
      const ry = circleShape.radiusY;
      const cos = Math.cos(r);
      const sin = Math.sin(r);
      return {
        center: c,
        top: {
          x: c.x - ry * sin,
          y: c.y - ry * cos,
        },
        right: {
          x: c.x + rx * cos,
          y: c.y + rx * sin,
        },
        bottom: {
          x: c.x + ry * sin,
          y: c.y + ry * cos,
        },
        left: {
          x: c.x - rx * cos,
          y: c.y - rx * sin,
        },
        // Rotation handle offset further out so it doesn't overlap with top handle (easier to tap on tablet)
        rotation: {
          x: c.x - (ry + 32) * sin,
          y: c.y - (ry + 32) * cos,
        },
      };
    }

    case 'freehand':
      const freehandShape = shape as Extract<Shape, { type: 'freehand' }>;
      const freehandControlPoints: { [key: string]: Point } = {};
      // Show control points for ALL points to allow full editing
      freehandShape.points.forEach((point, index) => {
        freehandControlPoints[`point_${index}`] = point;
      });
      return freehandControlPoints;

    default:
      return {};
  }
}

// Find which control handle (if any) is at the given point
export function findControlHandleAt(
  canvasPoint: Point,
  shape: Shape,
  imageScale: ImageScale,
  tolerance: number = 8
): string | null {
  const controlPoints = getShapeControlPoints(shape);

  // For circle/ellipse, pick the *closest* handle within tolerance so resize (top/right/etc.) wins when
  // the user taps there; rotation only wins when the tap is actually nearer the rotation handle (avoids
  // rotation stealing resize taps and breaking measurement updates).
  if (shape.type === 'circle') {
    let bestHandle: string | null = null;
    let bestDist = tolerance + 1;
    for (const [handleName, handlePoint] of Object.entries(controlPoints)) {
      const canvasHandle = imageToCanvasCoordinates(handlePoint, imageScale);
      const dist = calculatePixelDistance(canvasPoint, canvasHandle);
      if (dist <= tolerance && dist < bestDist) {
        bestDist = dist;
        bestHandle = handleName;
      }
    }
    return bestHandle;
  }

  for (const [handleName, handlePoint] of Object.entries(controlPoints)) {
    const canvasHandle = imageToCanvasCoordinates(handlePoint, imageScale);
    if (isPointNearHandle(canvasPoint, canvasHandle, tolerance)) {
      return handleName;
    }
  }

  return null;
}

// Update a shape based on handle movement
export function updateShapeHandle(
  shape: Shape,
  handleName: string,
  newPosition: Point
): Shape {
  switch (shape.type) {
    case 'line':
      const lineShape = shape as Extract<Shape, { type: 'line' }>;
      if (handleName === 'start') {
        return { ...lineShape, startPoint: newPosition };
      } else if (handleName === 'end') {
        return { ...lineShape, endPoint: newPosition };
      }
      break;

    case 'square':
      const rectShape = shape as Extract<Shape, { type: 'square' }>;
      const currentMinX = Math.min(
        rectShape.startPoint.x,
        rectShape.endPoint.x
      );
      const currentMaxX = Math.max(
        rectShape.startPoint.x,
        rectShape.endPoint.x
      );
      const currentMinY = Math.min(
        rectShape.startPoint.y,
        rectShape.endPoint.y
      );
      const currentMaxY = Math.max(
        rectShape.startPoint.y,
        rectShape.endPoint.y
      );

      let newStartPoint = rectShape.startPoint;
      let newEndPoint = rectShape.endPoint;

      switch (handleName) {
        case 'topLeft':
          newStartPoint = { x: newPosition.x, y: newPosition.y };
          newEndPoint = { x: currentMaxX, y: currentMaxY };
          break;
        case 'topRight':
          newStartPoint = { x: currentMinX, y: newPosition.y };
          newEndPoint = { x: newPosition.x, y: currentMaxY };
          break;
        case 'bottomLeft':
          newStartPoint = { x: newPosition.x, y: currentMinY };
          newEndPoint = { x: currentMaxX, y: newPosition.y };
          break;
        case 'bottomRight':
          newStartPoint = { x: currentMinX, y: currentMinY };
          newEndPoint = { x: newPosition.x, y: newPosition.y };
          break;
      }

      return { ...rectShape, startPoint: newStartPoint, endPoint: newEndPoint };

    case 'circle': {
      const circleShape = shape as Extract<Shape, { type: 'circle' }>;
      const c = circleShape.center;
      const r = circleShape.rotation ?? 0;
      const cos = Math.cos(r);
      const sin = Math.sin(r);
      if (handleName === 'rotation') {
        const rotation = Math.atan2(c.x - newPosition.x, c.y - newPosition.y);
        return { ...circleShape, rotation };
      }
      if (['top', 'bottom'].includes(handleName)) {
        const localY = -(newPosition.x - c.x) * sin + (newPosition.y - c.y) * cos;
        const newRadiusY = Math.abs(localY);
        return { ...circleShape, radiusY: newRadiusY };
      }
      if (['left', 'right'].includes(handleName)) {
        const localX = (newPosition.x - c.x) * cos + (newPosition.y - c.y) * sin;
        const newRadiusX = Math.abs(localX);
        return { ...circleShape, radiusX: newRadiusX };
      }
      break;
    }

    case 'freehand':
      const freehandShape = shape as Extract<Shape, { type: 'freehand' }>;
      if (handleName.startsWith('point_')) {
        const pointIndex = parseInt(handleName.split('_')[1]);
        if (pointIndex >= 0 && pointIndex < freehandShape.points.length) {
          const newPoints = [...freehandShape.points];

          // Check if this is a closed shape (first and last points are the same)
          const isClosedShape = isFreehandShapeClosed(freehandShape.points);

          if (isClosedShape) {
            if (pointIndex === 0) {
              // Moving the first point - update both first and last points to keep them synchronized
              newPoints[0] = newPosition;
              newPoints[newPoints.length - 1] = newPosition;
            } else if (pointIndex === newPoints.length - 1) {
              // Moving the last point - update both first and last points to keep them synchronized
              newPoints[0] = newPosition;
              newPoints[newPoints.length - 1] = newPosition;
            } else {
              // Moving any other point - just update that point
              newPoints[pointIndex] = newPosition;
            }
          } else {
            // Open shape - just update the specific point
            newPoints[pointIndex] = newPosition;
          }

          // Check if the user is trying to "break" the closure by moving first/last point far from its duplicate
          if (
            isClosedShape &&
            (pointIndex === 0 || pointIndex === newPoints.length - 1)
          ) {
            const firstPoint = newPoints[0];
            const lastPoint = newPoints[newPoints.length - 1];
            const distance = Math.sqrt(
              Math.pow(firstPoint.x - lastPoint.x, 2) +
                Math.pow(firstPoint.y - lastPoint.y, 2)
            );

            // If the points are moved far apart (more than 5 pixels), "open" the shape by removing the duplicate
            if (distance > 5) {
              // Remove the last point to open the shape
              newPoints.pop();
            }
          }

          return { ...freehandShape, points: newPoints };
        }
      }
      break;
  }

  return shape;
}
